import type { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import {
  CapabilitySuggestionApiClient,
  capabilitySuggestionEvents,
} from '@indutrust/capability-data';
import type { NewCapabilitySuggestion } from '@indutrust/capability-model';
import type { MachinePark } from '@indutrust/factory-model';
import { MachineCatalogApiClient } from '@indutrust/machine-data';
import type { MachineCatalogEntry } from '@indutrust/machine-model';
import { resolveHttpError, withRequestStatus } from '@indutrust/shared-util';
import { mapResponse } from '@ngrx/operators';
import { signalStore, withComputed, withState } from '@ngrx/signals';
import {
  Events,
  on,
  withEventHandlers,
  withReducer,
} from '@ngrx/signals/events';
import { catchError, forkJoin, from, map, of, switchMap } from 'rxjs';
import { MachineParkApiClient } from '../machine-park-api-client.service';
import { shouldSubmitPendingCatalog } from './machine-park-catalog-submit.util';
import { machineParkEvents } from './machine-park.events';

type MachineParkState = {
  catalogSubmitFailedMachineId: number | null;
  factoryId: number | null;
  machines: MachinePark[];
  saving: boolean;
  suggestionSubmitFailedMachineId: number | null;
};

const initialState: MachineParkState = {
  catalogSubmitFailedMachineId: null,
  factoryId: null,
  machines: [],
  saving: false,
  suggestionSubmitFailedMachineId: null,
};

function toCatalogPayload(
  machine: MachinePark,
  factoryId: number
): Omit<MachineCatalogEntry, 'id' | 'datasheets'> {
  return {
    name: machine.name,
    brand: machine.brand,
    status: 'pending',
    capability_ids: machine.capability_ids,
    submitted_by_factory_id: factoryId,
    submitted_by_machine_park_id: machine.id,
  };
}

/**
 * Component-scoped store owning the flat machine park list for a single factory.
 *
 * Provided by {@link import('@indutrust/factory-data').provideFactoryEditStores | provideFactoryEditStores}
 * so each factory editor gets a fresh list.
 *
 * Loads via `GET /factories/{factoryId}/machine_parks`. Every mutation
 * re-emits `mutationSucceeded`, which re-triggers a full reload. Adding a
 * custom (non-catalog) machine also submits a `pending` entry to the shared
 * machine catalog for admin review. Catalog dual-write is soft-fail: the
 * factory row stays saved and {@link machineParkEvents.catalogSubmitFailed}
 * fires (UI toast / message). The next edit-save of a still-pending custom
 * machine (no `machine_catalog_id`) retries the catalog submit automatically.
 *
 * Staged capability suggestions (leaf or parent) are dual-written to
 * `/capability_suggestions` in the same save (soft-fail via
 * {@link machineParkEvents.suggestionSubmitFailed}).
 *
 * @example
 * const machineParkStore = inject(MachineParkStore);
 * const dispatch = injectDispatch(machineParkEvents);
 * dispatch.loadRequested({ factoryId: 7 });
 */
export const MachineParkStore = signalStore(
  withState(initialState),
  withRequestStatus(),
  withComputed(
    ({
      machines,
      catalogSubmitFailedMachineId,
      suggestionSubmitFailedMachineId,
    }) => ({
      entities: computed(() => machines()),
      catalogSubmitFailed: computed(
        () => catalogSubmitFailedMachineId() !== null
      ),
      suggestionSubmitFailed: computed(
        () => suggestionSubmitFailedMachineId() !== null
      ),
    })
  ),
  withReducer(
    on(machineParkEvents.loadRequested, ({ payload }) => ({
      factoryId: payload.factoryId,
      requestStatus: 'loading' as const,
    })),
    on(machineParkEvents.loadSucceeded, ({ payload }) => ({
      machines: payload.machines,
      requestStatus: 'success' as const,
    })),
    on(machineParkEvents.loadFailed, ({ payload }) => ({
      requestStatus: { error: payload.error },
    })),
    on(machineParkEvents.addRequested, () => ({
      requestStatus: 'loading' as const,
      saving: true,
      catalogSubmitFailedMachineId: null,
      suggestionSubmitFailedMachineId: null,
    })),
    on(machineParkEvents.editRequested, () => ({
      requestStatus: 'loading' as const,
      saving: true,
      catalogSubmitFailedMachineId: null,
      suggestionSubmitFailedMachineId: null,
    })),
    on(machineParkEvents.deleteRequested, () => ({ saving: true })),
    on(machineParkEvents.catalogSubmitFailed, ({ payload }) => ({
      catalogSubmitFailedMachineId: payload.machineId,
    })),
    on(machineParkEvents.suggestionSubmitFailed, ({ payload }) => ({
      suggestionSubmitFailedMachineId: payload.machineId,
    })),
    on(machineParkEvents.mutationSucceeded, () => ({ saving: false })),
    on(machineParkEvents.mutationFailed, ({ payload }) => ({
      saving: false,
      requestStatus: { error: payload.error },
    }))
  ),
  withEventHandlers(
    (
      _store,
      events = inject(Events),
      machineParks = inject(MachineParkApiClient),
      catalog = inject(MachineCatalogApiClient),
      suggestionsApi = inject(CapabilitySuggestionApiClient)
    ) => {
      const toFailed = (error: HttpErrorResponse) =>
        machineParkEvents.mutationFailed({
          error: resolveHttpError(error).code,
        });

      const submitSuggestionsSoft = (
        machine: MachinePark,
        factoryId: number,
        suggestions: NewCapabilitySuggestion[] | undefined
      ) => {
        if (!suggestions?.length) {
          return of(false);
        }
        return forkJoin(
          suggestions.map((suggestion) =>
            suggestionsApi
              .add({
                kind: suggestion.kind,
                name: suggestion.name,
                description: suggestion.description,
                ...(typeof suggestion.parent_id === 'number'
                  ? { parent_id: suggestion.parent_id }
                  : {}),
                status: 'pending',
                submitted_by_factory_id: factoryId,
                submitted_by_machine_park_id: machine.id,
              })
              .pipe(catchError(() => of(null)))
          )
        ).pipe(map((results) => results.includes(null)));
      };

      const afterParkSave = (
        machine: MachinePark,
        factoryId: number,
        parkPayload: Omit<MachinePark, 'id' | 'datasheets'>,
        capabilitySuggestions: NewCapabilitySuggestion[] | undefined
      ) => {
        const catalog$ = shouldSubmitPendingCatalog(parkPayload)
          ? catalog.add(toCatalogPayload(machine, factoryId)).pipe(
              map(() => false),
              catchError(() => of(true))
            )
          : of(false);

        return catalog$.pipe(
          switchMap((catalogFailed) =>
            submitSuggestionsSoft(
              machine,
              factoryId,
              capabilitySuggestions
            ).pipe(
              switchMap((suggestionFailed) => {
                const resultEvents = [];
                if (catalogFailed) {
                  resultEvents.push(
                    machineParkEvents.catalogSubmitFailed({
                      factoryId,
                      machineId: machine.id,
                    })
                  );
                }
                if (suggestionFailed) {
                  resultEvents.push(
                    machineParkEvents.suggestionSubmitFailed({
                      factoryId,
                      machineId: machine.id,
                    })
                  );
                }
                resultEvents.push(
                  machineParkEvents.mutationSucceeded({ factoryId })
                );
                return of(...resultEvents);
              })
            )
          )
        );
      };

      return {
        load$: events.on(machineParkEvents.loadRequested).pipe(
          switchMap(({ payload }) =>
            machineParks.getAll({ factoryId: payload.factoryId }).pipe(
              mapResponse({
                next: (machines) =>
                  machineParkEvents.loadSucceeded({
                    factoryId: payload.factoryId,
                    machines,
                  }),
                error: (error: HttpErrorResponse) =>
                  machineParkEvents.loadFailed({
                    error: resolveHttpError(error).code,
                  }),
              })
            )
          )
        ),

        add$: events.on(machineParkEvents.addRequested).pipe(
          switchMap(({ payload }) =>
            machineParks
              .add(payload.payload, { factoryId: payload.factoryId })
              .pipe(
                switchMap((created) =>
                  afterParkSave(
                    created,
                    payload.factoryId,
                    payload.payload,
                    payload.capabilitySuggestions
                  )
                ),
                catchError((error: HttpErrorResponse) => of(toFailed(error)))
              )
          )
        ),

        edit$: events.on(machineParkEvents.editRequested).pipe(
          switchMap(({ payload }) =>
            machineParks
              .patch(payload.machineId, payload.payload, {
                factoryId: payload.factoryId,
              })
              .pipe(
                switchMap((updated) =>
                  afterParkSave(
                    updated,
                    payload.factoryId,
                    payload.payload,
                    payload.capabilitySuggestions
                  )
                ),
                catchError((error: HttpErrorResponse) => of(toFailed(error)))
              )
          )
        ),

        delete$: events.on(machineParkEvents.deleteRequested).pipe(
          switchMap(({ payload }) =>
            machineParks
              .delete(payload.machineId, { factoryId: payload.factoryId })
              .pipe(
                mapResponse({
                  next: () =>
                    machineParkEvents.mutationSucceeded({
                      factoryId: payload.factoryId,
                    }),
                  error: toFailed,
                })
              )
          )
        ),

        refreshOnMutation$: events.on(machineParkEvents.mutationSucceeded).pipe(
          switchMap(({ payload }) =>
            from([
              machineParkEvents.loadRequested({
                factoryId: payload.factoryId,
              }),
              capabilitySuggestionEvents.loadRequested({
                factoryId: payload.factoryId,
              }),
            ])
          )
        ),
      };
    }
  )
);
